/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useContext } from 'react';
import API from '../api/api';
import { AuthContext } from '../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  Tabs,
  Tab,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useI18n } from '../contexts/I18nContext';

const formatCurrency = (value, lang) => {
  if (value == null) return '-';
  return new Intl.NumberFormat(lang === 'ar' ? 'ar-EG' : 'fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
};

export default function Partners() {
  const { user } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const { lang } = useI18n();
  const tr = (fr, ar) => (lang === 'ar' ? ar : fr);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [partners, setPartners] = useState([]);
  const [partnerPrices, setPartnerPrices] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: '', contact: '', type: 'SOFORT' });
  const [loading, setLoading] = useState(true);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [tab, setTab] = useState(0);
  const [editKey, setEditKey] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [showPartnerForm, setShowPartnerForm] = useState(false);
  const [showPriceForm, setShowPriceForm] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [paymentForm, setPaymentForm] = useState({
    partner_id: '',
    amount: '',
    note: '',
  });
  const [addForm, setAddForm] = useState({
    partner_id: '',
    product_id: '',
    price: '',
  });

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const res = await API.get('/partners');
      setPartners(res.data);
      const internalPartners = res.data.filter((p) => p.type === 'INTERN');
      if (internalPartners.length > 0) {
        setPaymentForm((prev) => ({
          ...prev,
          partner_id: prev.partner_id || String(internalPartners[0].id),
        }));
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du chargement des partenaires', 'فشل تحميل الشركاء'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerPrices = async () => {
    try {
      setLoadingPrices(true);
      const res = await API.get('/partners/prices');
      setPartnerPrices(res.data);
    } catch (err) {
      showSnackbar(
        err.response?.data?.message || tr('Erreur lors du chargement des prix partenaires', 'فشل تحميل أسعار الشركاء'),
        'error'
      );
      setPartnerPrices([]);
    } finally {
      setLoadingPrices(false);
    }
  };

  const fetchPartnerTransactions = async (partnerId) => {
    if (!partnerId) {
      setTransactions([]);
      return;
    }
    try {
      setLoadingTransactions(true);
      const res = await API.get(`/partners/${partnerId}/transactions`);
      setTransactions(res.data);
    } catch (err) {
      showSnackbar(
        err.response?.data?.message || tr('Erreur lors du chargement des transactions', 'فشل تحميل العمليات'),
        'error'
      );
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await API.get('/products');
      setProducts(res.data);
      if (res.data.length > 0) {
        setAddForm((prev) => ({
          ...prev,
          product_id: prev.product_id || String(res.data[0].product_id || res.data[0].id),
        }));
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du chargement des articles', 'فشل تحميل المنتجات'), 'error');
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchPartners();
    fetchProducts();
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab === 1) {
      fetchPartnerPrices();
    }
    if (tab === 2 && paymentForm.partner_id) {
      fetchPartnerTransactions(paymentForm.partner_id);
    }
  }, [tab, paymentForm.partner_id]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.post('/partners', form);
      setForm({ name: '', contact: '', type: 'SOFORT' });
      showSnackbar(tr('Partenaire cree', 'تم إنشاء الشريك'), 'success');
      fetchPartners();
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors de la creation', 'فشل الإنشاء'), 'error');
    }
  };

  const handlePriceSubmit = async (row) => {
    try {
      await API.post('/partners/price', {
        partner_id: Number(row.partner_id),
        product_id: Number(row.product_id),
        price: Number(editPrice),
      });
      showSnackbar(tr('Prix partenaire enregistre', 'تم حفظ سعر الشريك'), 'success');
      setEditKey(null);
      setEditPrice('');
      fetchPartnerPrices();
    } catch (err) {
      showSnackbar(
        err.response?.data?.message || tr("Erreur lors de l'enregistrement du prix partenaire", 'فشل حفظ سعر الشريك'),
        'error'
      );
    }
  };

  const handleAddChange = (e) =>
    setAddForm({ ...addForm, [e.target.name]: e.target.value });

  const handlePaymentChange = (e) =>
    setPaymentForm({ ...paymentForm, [e.target.name]: e.target.value });

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.post('/partners/transaction', {
        partner_id: Number(paymentForm.partner_id),
        type: 'PAYMENT',
        amount: Number(paymentForm.amount),
        note: paymentForm.note,
      });
      showSnackbar(tr('Paiement enregistre', 'تم تسجيل الدفعة'), 'success');
      setPaymentForm((prev) => ({ ...prev, amount: '', note: '' }));
      fetchPartners();
      fetchPartnerTransactions(paymentForm.partner_id);
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du paiement', 'فشل الدفع'), 'error');
    }
  };

  const usedProductIdsForPartner = new Set(
    partnerPrices
      .filter((p) => String(p.partner_id) === String(addForm.partner_id))
      .map((p) => String(p.product_id))
  );

  const availableProducts = products.filter(
    (t) => !usedProductIdsForPartner.has(String(t.product_id || t.id))
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!addForm.partner_id) return;
    if (availableProducts.length === 0) {
      setAddForm((prev) => ({ ...prev, product_id: '' }));
      return;
    }
    if (!availableProducts.some((t) => String(t.product_id || t.id) === String(addForm.product_id))) {
      setAddForm((prev) => ({
        ...prev,
        product_id: String(availableProducts[0].product_id || availableProducts[0].id),
      }));
    }
  }, [addForm.partner_id, availableProducts]);

  const internalPartners = partners.filter((p) => p.type === 'INTERN');
  const selectedBalancePartner = internalPartners.find(
    (p) => String(p.id) === String(paymentForm.partner_id)
  );

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.post('/partners/price', {
        partner_id: Number(addForm.partner_id),
        product_id: Number(addForm.product_id),
        price: Number(addForm.price),
      });
      showSnackbar(tr('Prix partenaire enregistre', 'تم حفظ سعر الشريك'), 'success');
      setAddForm({ partner_id: '', product_id: '', price: '' });
      fetchPartnerPrices();
    } catch (err) {
      showSnackbar(
        err.response?.data?.message || tr("Erreur lors de l'enregistrement du prix partenaire", 'فشل حفظ سعر الشريك'),
        'error'
      );
    }
  };

  const startEdit = (row) => {
    setEditKey(`${row.partner_id}-${row.product_id}`);
    setEditPrice(String(row.price ?? ''));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 6 },
        py: 5,
        background: 'var(--app-bg)',
      }}
    >
      {/* HERO */}
      <Box
        sx={{
          mb: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: 'var(--text-primary)',
            letterSpacing: 1,
            fontSize: { xs: 28, sm: 36, md: 42 },
          }}
        >
          {tr('Centre de controle des partenaires', 'مركز إدارة الشركاء')}
        </Typography>

        <Typography sx={{ color: 'var(--text-muted)', mt: 1, maxWidth: 500 }}>
          {tr('Gestion des partenaires et des contacts.', 'إدارة الشركاء وجهات الاتصال.')}
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          mt={4}
          justifyContent="center"
        >
            <Chip
              label={`${tr('Partenaires', 'الشركاء')}: ${partners.length}`}
              sx={{ bgcolor: '#16a34a', color: 'var(--text-primary)' }}
            />
        </Stack>
      </Box>

      {/* FORM */}
      <Paper
        sx={{
          p: 4,
          mb: 6,
          borderRadius: 4,
          background:
            'var(--surface-2)',
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--border-subtle)',
        }}
      >
        <Tabs
          value={tab}
          onChange={(e, newValue) => setTab(newValue)}
          centered
          indicatorColor="secondary"
          textColor="inherit"
          sx={{
            mb: 3,
            '& .MuiTab-root': {
              color: 'var(--text-muted)',
              fontWeight: 700,
            },
            '& .Mui-selected': {
              color: 'var(--text-primary)',
            },
          }}
        >
          <Tab label={tr('Creer un partenaire', 'إنشاء شريك')} />
          <Tab label={tr('Prix partenaires', 'أسعار الشركاء')} />
          <Tab label={tr('Comptes partenaires', 'حسابات الشركاء')} />
        </Tabs>

        {tab === 0 && (
          <>
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
              <Button
                variant="contained"
                onClick={() => setShowPartnerForm((v) => !v)}
                disabled={user?.role !== 'ADMIN'}
                startIcon={<AddCircleOutlineIcon />}
                sx={{
                  px: 3,
                  borderRadius: 3,
                  fontWeight: 600,
                  background: 'linear-gradient(90deg, #16a34a, #059669)',
                }}
              >
                {showPartnerForm ? tr('Fermer', 'إغلاق') : tr('Ajouter', 'إضافة')}
              </Button>
            </Stack>

            {showPartnerForm && (
              <Box component="form" onSubmit={handleSubmit}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    name="name"
                    label={tr('Nom', 'الاسم')}
                    value={form.name}
                    onChange={handleChange}
                    required
                    fullWidth
                    disabled={user?.role !== 'ADMIN'}
                    sx={{
                      backgroundColor: 'var(--input-bg)',
                      borderRadius: 2,
                    }}
                  />
                  <TextField
                    name="contact"
                    label={tr('Contact', 'جهة الاتصال')}
                    value={form.contact}
                    onChange={handleChange}
                    fullWidth
                    disabled={user?.role !== 'ADMIN'}
                    sx={{
                      backgroundColor: 'var(--input-bg)',
                      borderRadius: 2,
                    }}
                  />
                  <TextField
                    select
                    name="type"
                    label={tr('Type', 'النوع')}
                    value={form.type}
                    onChange={handleChange}
                    fullWidth
                    disabled={user?.role !== 'ADMIN'}
                    sx={{
                      backgroundColor: 'var(--input-bg)',
                      borderRadius: 2,
                    }}
                  >
                    <MenuItem value="SOFORT">{tr('Immediat', 'فوري')}</MenuItem>
                    <MenuItem value="INTERN">{tr('Interne', 'داخلي')}</MenuItem>
                  </TextField>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={user?.role !== 'ADMIN'}
                    sx={{
                      px: 4,
                      borderRadius: 3,
                      fontWeight: 600,
                      background: 'linear-gradient(90deg, #16a34a, #059669)',
                    }}
                  >
                    {tr('Enregistrer', 'حفظ')}
                  </Button>
                </Stack>
              </Box>
            )}

            <Box sx={{ mt: 3 }}>
              {loading ? (
                <Stack alignItems="center">
                  <CircularProgress sx={{ color: 'var(--text-primary)' }} />
                </Stack>
              ) : isMobile ? (
                <Stack spacing={2}>
                  {partners.map((p) => (
                    <Paper
                      key={p.id}
                      sx={{
                        p: 2,
                        borderRadius: 4,
                        background:
                          'var(--surface-1)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid var(--border-subtle)',
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                          {p.name}
                        </Typography>
                        <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                          {tr('Contact', 'جهة الاتصال')}: {p.contact || '-'}
                        </Typography>
                        <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                          {tr('Type', 'النوع')}: {p.type || '-'}
                        </Typography>
                        <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                          {tr('Solde', 'الرصيد')}: {formatCurrency(p.balance, lang)}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Paper
                  sx={{
                    borderRadius: 4,
                    background:
                      'var(--surface-1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid var(--border-subtle)',
                    overflow: 'hidden',
                  }}
                >
                  <TableContainer sx={{ maxHeight: { xs: 420, md: 560 } }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {['ID', tr('Nom', 'الاسم'), tr('Contact', 'جهة الاتصال'), tr('Type', 'النوع'), tr('Solde', 'الرصيد')].map((label) => (
                            <TableCell
                              key={label}
                              sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 700,
                                backgroundColor: 'var(--table-header-bg)',
                                borderBottom: '1px solid var(--border-subtle)',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {partners.map((p) => (
                          <TableRow
                            key={p.id}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'var(--surface-3)',
                              },
                            }}
                          >
                            <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                              {p.id}
                            </TableCell>
                            <TableCell sx={{ color: 'var(--text-secondary)' }}>
                              {p.name}
                            </TableCell>
                          <TableCell sx={{ color: 'var(--text-secondary)' }}>
                            {p.contact || '-'}
                          </TableCell>
                          <TableCell sx={{ color: 'var(--text-secondary)' }}>
                            {p.type || '-'}
                          </TableCell>
                          <TableCell sx={{ color: 'var(--text-secondary)' }}>
                            {formatCurrency(p.balance, lang)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </TableContainer>
                </Paper>
              )}
            </Box>
          </>
        )}

        {tab === 1 && (
          <>
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
              <Button
                variant="contained"
                onClick={() => setShowPriceForm((v) => !v)}
                disabled={user?.role !== 'ADMIN'}
                startIcon={<AddCircleOutlineIcon />}
                sx={{
                  px: 3,
                  borderRadius: 3,
                  fontWeight: 600,
                  background: 'linear-gradient(90deg, #16a34a, #059669)',
                }}
              >
                {showPriceForm ? tr('Fermer', 'إغلاق') : tr('Ajouter', 'إضافة')}
              </Button>
            </Stack>

            {showPriceForm && (
              <Paper
                sx={{
                  p: 3,
                  mb: 3,
                  borderRadius: 4,
                  background:
                    'var(--surface-2)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid var(--border-subtle)',
                }}
              >
                  <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 2 }}>
                    {tr('Ajouter un prix partenaire', 'إضافة سعر شريك')}
                  </Typography>
                <Box component="form" onSubmit={handleAddSubmit}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                      select
                      name="partner_id"
                      label={tr('Partenaire', 'الشريك')}
                      value={addForm.partner_id}
                      onChange={handleAddChange}
                      required
                      fullWidth
                      disabled={user?.role !== 'ADMIN'}
                      sx={{
                        backgroundColor: 'var(--input-bg)',
                        borderRadius: 2,
                      }}
                    >
                      {partners.map((p) => (
                        <MenuItem key={p.id} value={String(p.id)}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      name="product_id"
                      label={tr('Article', 'المنتج')}
                      value={addForm.product_id}
                      onChange={handleAddChange}
                      required
                      fullWidth
                      disabled={user?.role !== 'ADMIN'}
                      sx={{
                        backgroundColor: 'var(--input-bg)',
                        borderRadius: 2,
                      }}
                    >
                      {availableProducts.map((t) => (
                        <MenuItem key={t.product_id || t.id} value={String(t.product_id || t.id)}>
                          {t.reference}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      name="price"
                      label={tr('Prix (EUR)', 'السعر (EUR)')}
                      type="number"
                      value={addForm.price}
                      onChange={handleAddChange}
                      required
                      fullWidth
                      disabled={user?.role !== 'ADMIN'}
                      sx={{
                        backgroundColor: 'var(--input-bg)',
                        borderRadius: 2,
                      }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={user?.role !== 'ADMIN'}
                      sx={{
                        px: 4,
                        borderRadius: 3,
                        fontWeight: 600,
                        background: 'linear-gradient(90deg, #16a34a, #059669)',
                      }}
                    >
                      {tr('Enregistrer', 'حفظ')}
                    </Button>
                  </Stack>
                </Box>
              </Paper>
            )}

            {loadingPrices ? (
              <Stack alignItems="center">
                <CircularProgress sx={{ color: 'var(--text-primary)' }} />
              </Stack>
            ) : isMobile ? (
              <Stack spacing={2}>
                {partnerPrices.map((row) => (
                  <Paper
                    key={`${row.partner_id}-${row.product_id}`}
                    sx={{
                      p: 2,
                      borderRadius: 4,
                      background:
                        'var(--surface-1)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid var(--border-subtle)',
                    }}
                  >
                    <Stack spacing={1}>
                      <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                        {row.partner_name || '-'}
                      </Typography>
                      <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                        {tr('Article', 'المنتج')}: {row.product_reference || '-'}
                      </Typography>
                      <Chip
                        label={`${row.price ?? '-'} EUR`}
                        size="small"
                        color="info"
                        sx={{ alignSelf: 'flex-start' }}
                      />
                      <Stack direction="row" spacing={1}>
                        {editKey === `${row.partner_id}-${row.product_id}` ? (
                          <>
                            <TextField
                              size="small"
                              type="number"
                              value={editPrice}
                              onChange={(e) => setEditPrice(e.target.value)}
                              sx={{
                                backgroundColor: 'var(--input-bg)',
                                borderRadius: 2,
                                maxWidth: 120,
                              }}
                            />
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handlePriceSubmit(row)}
                              disabled={user?.role !== 'ADMIN'}
                            >
                              {tr('Enregistrer', 'حفظ')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setEditKey(null);
                                setEditPrice('');
                              }}
                            >
                                  {tr('Annuler', 'إلغاء')}
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => startEdit(row)}
                            disabled={user?.role !== 'ADMIN'}
                          >
                                {tr('Modifier', 'تعديل')}
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Paper
                sx={{
                  borderRadius: 4,
                  background:
                    'var(--surface-1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid var(--border-subtle)',
                  overflow: 'hidden',
                }}
              >
                <TableContainer sx={{ maxHeight: { xs: 360, md: 480 } }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {[tr('Partenaire', 'الشريك'), tr('Article', 'المنتج'), tr('Prix (EUR)', 'السعر (EUR)'), tr('Action', 'إجراء')].map((label) => (
                          <TableCell
                            key={label}
                            sx={{
                              color: 'var(--text-primary)',
                              fontWeight: 700,
                              backgroundColor: 'var(--table-header-bg)',
                              borderBottom: '1px solid var(--border-subtle)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {partnerPrices.map((row) => (
                        <TableRow
                          key={`${row.partner_id}-${row.product_id}`}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'var(--surface-3)',
                            },
                          }}
                        >
                          <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                            {row.partner_name || '-'}
                          </TableCell>
                          <TableCell sx={{ color: 'var(--text-secondary)' }}>
                            {row.product_reference || '-'}
                          </TableCell>
                          <TableCell sx={{ color: 'var(--text-secondary)' }}>
                            {editKey === `${row.partner_id}-${row.product_id}` ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editPrice}
                                onChange={(e) => setEditPrice(e.target.value)}
                                sx={{
                                  backgroundColor: 'var(--input-bg)',
                                  borderRadius: 2,
                                  maxWidth: 120,
                                }}
                              />
                            ) : (
                              row.price
                            )}
                          </TableCell>
                          <TableCell>
                            {editKey === `${row.partner_id}-${row.product_id}` ? (
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handlePriceSubmit(row)}
                                  disabled={user?.role !== 'ADMIN'}
                                >
                                  {tr('Enregistrer', 'حفظ')}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    setEditKey(null);
                                    setEditPrice('');
                                  }}
                                >
                                  {tr('Annuler', 'إلغاء')}
                                </Button>
                              </Stack>
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => startEdit(row)}
                                disabled={user?.role !== 'ADMIN'}
                              >
                                {tr('Modifier', 'تعديل')}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

          </>
        )}

        {tab === 2 && (
          <>
            <Paper
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 4,
                background:
                  'var(--surface-2)',
                backdropFilter: 'blur(12px)',
                border: '1px solid var(--border-subtle)',
              }}
            >
              <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 1 }}>
                {tr('Enregistrer un paiement', 'تسجيل دفعة')}
              </Typography>
              <Typography sx={{ color: 'var(--text-muted)', mb: 2 }}>
                {tr('Solde actuel', 'الرصيد الحالي')}: {formatCurrency(selectedBalancePartner?.balance, lang)}
              </Typography>
              <Box component="form" onSubmit={handlePaymentSubmit}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    select
                    name="partner_id"
                    label={tr('Partenaire interne', 'شريك داخلي')}
                    value={paymentForm.partner_id}
                    onChange={handlePaymentChange}
                    required
                    fullWidth
                    disabled={user?.role !== 'ADMIN' || internalPartners.length === 0}
                    sx={{
                      backgroundColor: 'var(--input-bg)',
                      borderRadius: 2,
                    }}
                  >
                    {internalPartners.map((p) => (
                      <MenuItem key={p.id} value={String(p.id)}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    name="amount"
                    label={tr('Montant (EUR)', 'المبلغ (EUR)')}
                    type="number"
                    value={paymentForm.amount}
                    onChange={handlePaymentChange}
                    required
                    fullWidth
                    disabled={user?.role !== 'ADMIN'}
                    sx={{
                      backgroundColor: 'var(--input-bg)',
                      borderRadius: 2,
                    }}
                  />
                  <TextField
                    name="note"
                    label={tr('Note', 'ملاحظة')}
                    value={paymentForm.note}
                    onChange={handlePaymentChange}
                    fullWidth
                    disabled={user?.role !== 'ADMIN'}
                    sx={{
                      backgroundColor: 'var(--input-bg)',
                      borderRadius: 2,
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={user?.role !== 'ADMIN'}
                    sx={{
                      px: 4,
                      borderRadius: 3,
                      fontWeight: 600,
                      background: 'linear-gradient(90deg, #16a34a, #059669)',
                    }}
                  >
                    {tr('Enregistrer', 'حفظ')}
                  </Button>
                </Stack>
              </Box>
            </Paper>

            {loadingTransactions ? (
              <Stack alignItems="center">
                <CircularProgress sx={{ color: 'var(--text-primary)' }} />
              </Stack>
            ) : isMobile ? (
              <Stack spacing={2}>
                {transactions.map((row) => (
                  <Paper
                    key={row.id}
                    sx={{
                      p: 2,
                      borderRadius: 4,
                      background:
                        'var(--surface-1)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid var(--border-subtle)',
                    }}
                  >
                    <Stack spacing={0.6}>
                      <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                        {row.type}
                      </Typography>
                      <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                        {tr('Montant', 'المبلغ')}: {formatCurrency(row.amount, lang)}
                      </Typography>
                      <Typography sx={{ color: 'var(--text-muted)', fontSize: 12 }}>
                        {row.note || '-'}
                      </Typography>
                      <Typography sx={{ color: 'var(--text-dim)', fontSize: 11 }}>
                        {row.created_at}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Paper
                sx={{
                  borderRadius: 4,
                  background:
                    'var(--surface-1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid var(--border-subtle)',
                  overflow: 'hidden',
                }}
              >
                <TableContainer sx={{ maxHeight: { xs: 360, md: 480 } }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {[tr('Type', 'النوع'), tr('Montant', 'المبلغ'), tr('Note', 'ملاحظة'), tr('Date', 'التاريخ')].map((label) => (
                          <TableCell
                            key={label}
                            sx={{
                              color: 'var(--text-primary)',
                              fontWeight: 700,
                              backgroundColor: 'var(--table-header-bg)',
                              borderBottom: '1px solid var(--border-subtle)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((row) => (
                        <TableRow
                          key={row.id}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'var(--surface-3)',
                            },
                          }}
                        >
                          <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                            {row.type}
                          </TableCell>
                          <TableCell sx={{ color: 'var(--text-secondary)' }}>
                            {formatCurrency(row.amount, lang)}
                          </TableCell>
                          <TableCell sx={{ color: 'var(--text-secondary)' }}>
                            {row.note || '-'}
                          </TableCell>
                          <TableCell sx={{ color: 'var(--text-secondary)' }}>
                            {row.created_at}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
      </Paper>

    </Box>
  );
}








